import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Filter, Plus } from 'lucide-react';
import { Building, BaseComponent } from '../../../types/protocols';
import { LevelImpl } from '../../../types/implementations';
import { BaseType, OutdoorCategory, UnitKind, UnitTenure, UtilityCategory } from '../../../types/Enums';
import { translate, translateParkingAreaKind } from '../../../types/EnumTranslations';
import { analyzeComponents } from '../../../Helpers/utils';
import { currentRoofPlanBlueprint, hasRoofPlanView } from '../../../Helpers/roofPlan';
import { levelDisplayName } from '../../../Helpers/levelDisplayName';
import { parkingAreaDisplayName, spotHasCharger, vacantSpotCount } from '../../../Helpers/parkingUtils';
import { storageLevelsDescending, totalUnitArea as totalStorageUnitArea, unitsOnLevel as storageUnitsOnLevel, vacantUnitCount as vacantStorageUnitCount } from '../../../Helpers/storageUnitUtils';
import { totalUnitArea, vacantUnitCount } from '../../../Helpers/unitUtils';
import { RentableObjects } from './buildingTabsView';
import { useFeature } from '../../../hooks/useFeatures';
import BuildingCustomButton from '../BuildingCustomButton';

interface ComponentCategoriesTabProps {
    building: Building;
    buildingKey: string;
    objects: RentableObjects;
}

/**
 * Shown in a category column that has nothing to list, so the grid has no gaping hole.
 * Categories you can add to from here (parking, storage, units) pass a call to action.
 */
const EmptyCategoryCard: React.FC<{ text: string; addLabel?: string; addTo?: string }> = ({ text, addLabel, addTo }) => (
    <li className="bt-category-empty-card">
        <span>{text}</span>
        {addLabel && addTo && (
            <Link to={addTo} className="bt-category-add-button">
                <Plus size={16} aria-hidden="true" />
                {addLabel}
            </Link>
        )}
    </li>
);

const formatArea = (area: number) => `${area.toLocaleString('sv-SE', { maximumFractionDigits: 1 })} m²`;

/**
 * A category column's heading. The "visa ej registrerade" toggle also lists
 * the types that have nothing registered, dimmed; without one the heading is
 * centred.
 */
const CategoryHeader: React.FC<{
    icon: string;
    title: string;
    showUnregistered?: boolean;
    onToggleUnregistered?: () => void;
}> = ({ icon, title, showUnregistered, onToggleUnregistered }) => (
    <div className="bt-category-header">
        {onToggleUnregistered && <div className="bt-category-header-spacer" />}
        <div className="bt-category-title">
            <img src={icon} alt="" />
            <h3>{title}</h3>
        </div>
        {onToggleUnregistered && (
            <button
                type="button"
                className={`bt-category-filter${showUnregistered ? ' bt-category-filter--on' : ''}`}
                onClick={onToggleUnregistered}
                title={showUnregistered ? 'Dölj ej registrerade' : 'Visa ej registrerade'}
            >
                <Filter size={20} aria-hidden="true" />
            </button>
        )}
    </div>
);

/**
 * Komponenter: the building's components grouped into category columns
 * (Byggnad, Våningsplan, Utemiljö, Fastighetsteknik, Fast inredning, Parkering,
 * Förråd, Lägenheter & lokaler), each entry opening its type, level or object
 * page. The same grid BuildingPage showed before the tabs.
 */
const ComponentCategoriesTab: React.FC<ComponentCategoriesTabProps> = ({ building, buildingKey, objects }) => {
    // Cards of features turned off are not shown (their objects are already emptied by BuildingTabs)
    const parkingEnabled = useFeature('parking');
    const storageEnabled = useFeature('storageUnits');
    const unitsEnabled = useFeature('units');
    const model = building.model;
    const base = `/building/${buildingKey}`;
    const [showUnregisteredBuilding, setShowUnregisteredBuilding] = useState(false);
    const [showUnregisteredVaningsplan, setShowUnregisteredVaningsplan] = useState(false);
    const [showUnregisteredUtemiljo, setShowUnregisteredUtemiljo] = useState(false);
    const [showUnregisteredFastighetsteknik, setShowUnregisteredFastighetsteknik] = useState(false);

    const allComponents = Object.values(model.structure?.components || {});
    const ofType = (type: BaseType) => allComponents.filter(component => component.type === type);

    const roofComponents = ofType(BaseType.Roof);
    const roofSafetyComponents = ofType(BaseType.RoofSafety);
    const facadeComponents = ofType(BaseType.Facade);
    // Archived balconies (replaced ones) are not listed, so they do not count here either
    const balconyComponents = ofType(BaseType.Balcony).filter(component => !component.archivedAt);
    const foundationComponents = ofType(BaseType.Foundation);
    const drainageComponents = ofType(BaseType.RoofDrainage);
    const groundDrainageComponents = ofType(BaseType.GroundDrainage);
    const riserComponents = ofType(BaseType.Riser);
    // Archived ones are not listed, so they do not count here either
    const miscellaneousComponents = ofType(BaseType.Miscellaneous).filter(component => !component.archivedAt);
    const chimneyComponents = ofType(BaseType.Chimney);
    const windowCount = ofType(BaseType.Window).length;
    const doorCount = ofType(BaseType.Door).length;
    const sheetMetalCount = ofType(BaseType.SheetMetal).length;
    // Fast inredning: room-bound; one without a room is listed as "Utan rum". Archived ones are not listed.
    const fixedFurnishingComponents = ofType(BaseType.FixedFurnishing).filter(component => !component.archivedAt);
    const unplacedFixedFurnishingCount = fixedFurnishingComponents.filter(component => (component.parentRoomKeys?.value?.length ?? 0) === 0).length;

    // Single-pass category counting for utility components
    const categoryCounts: Record<UtilityCategory, number> = {
        [UtilityCategory.Ventilation]: 0,
        [UtilityCategory.HeatingCooling]: 0,
        [UtilityCategory.Elevator]: 0,
        [UtilityCategory.FireSafety]: 0,
        [UtilityCategory.MechanicalMaintenanceRiskConstructions]: 0,
        [UtilityCategory.Electrical]: 0,
        [UtilityCategory.WaterAndSewage]: 0,
        [UtilityCategory.DoorAutomation]: 0,
        [UtilityCategory.Appliances]: 0,
        [UtilityCategory.UtilityOther]: 0,
    };
    let uncategorizedCount = 0;
    ofType(BaseType.Utility).forEach(component => {
        const category = component.utilityCategory as UtilityCategory;
        if (category && Object.values(UtilityCategory).includes(category)) {
            categoryCounts[category]++;
        } else {
            uncategorizedCount++;
        }
    });

    // Single-pass category counting for outdoor components
    const outdoorCategoryCounts: Record<OutdoorCategory, number> = {
        [OutdoorCategory.GroundSurface]: 0,
        [OutdoorCategory.PlayEquipment]: 0,
        [OutdoorCategory.Boundaries]: 0,
        [OutdoorCategory.Structures]: 0,
        [OutdoorCategory.PermanentFurniture]: 0,
        [OutdoorCategory.OutdoorOther]: 0,
    };
    let outdoorUncategorizedCount = 0;
    ofType(BaseType.Outdoor).forEach(component => {
        const category = component.outdoorCategory as OutdoorCategory;
        if (category && Object.values(OutdoorCategory).includes(category)) {
            outdoorCategoryCounts[category]++;
        } else {
            outdoorUncategorizedCount++;
        }
    });

    // A category column with nothing registered moves last in the grid and, while its
    // "visa ej registrerade" filter is off, shows a placeholder card instead of a gaping hole.
    // The order follows the data only, never the filter, so toggling a filter doesn't move columns.
    const buildingCategoryEmpty = [
        roofComponents,
        roofSafetyComponents,
        drainageComponents,
        chimneyComponents,
        facadeComponents,
        balconyComponents,
        foundationComponents,
        groundDrainageComponents,
        riserComponents,
        miscellaneousComponents,
    ].every(components => components.length === 0);

    const allLevelKeys = Object.keys(model.structure?.levels || {});
    const allRooms = Object.values(model.structure?.rooms || {});
    // A room is without level when its levelKey is unset or names no existing level
    const roomsWithoutLevel = allRooms.filter(room => {
        const levelKey = room.levelKey;
        return !levelKey || levelKey.trim() === '' || !allLevelKeys.includes(levelKey);
    });
    const levelsCategoryEmpty = allLevelKeys.length === 0 && roomsWithoutLevel.length === 0;

    const outdoorCategoryEmpty = outdoorUncategorizedCount === 0
        && Object.values(outdoorCategoryCounts).every(count => count === 0);

    const utilityCategoryEmpty = uncategorizedCount === 0
        && Object.values(categoryCounts).every(count => count === 0);

    const { parkingAreas, storageUnits, units } = objects;
    const storageLevels = storageLevelsDescending(model.structure?.levels)
        .map(level => ({ level, units: storageUnitsOnLevel(storageUnits, level.key) }))
        .filter(entry => entry.units.length > 0);

    /** Children of the given surfaces, counted per type, appended to the surfaces' metadata. */
    const childCounts = (surfaces: BaseComponent[]): string[] => {
        const surfaceKeys = new Set(surfaces.map(surface => surface.key));
        const children = allComponents.filter(component => surfaceKeys.has(component.parentSurface?.value ?? ''));
        const count = (type: BaseType, archivedExcluded = false) =>
            children.filter(component => component.type === type && !(archivedExcluded && component.archivedAt)).length;
        const sheetMetal = count(BaseType.SheetMetal);
        const chimneys = count(BaseType.Chimney);
        const drainage = count(BaseType.RoofDrainage);
        const balconies = count(BaseType.Balcony, true);
        return [
            ...(sheetMetal > 0 ? [`${sheetMetal} plåtarbeten`] : []),
            ...(chimneys > 0 ? [`${chimneys} ${translate(BaseType.Chimney).toLowerCase()}`] : []),
            ...(drainage > 0 ? [`${drainage} ${translate(BaseType.RoofDrainage).toLowerCase()}`] : []),
            ...(balconies > 0 ? [`${balconies} ${balconies === 1 ? 'balkong' : 'balkonger'}`] : []),
        ];
    };

    const typeButton = (
        components: BaseComponent[],
        title: string,
        imagePath: string,
        path: string,
        show: boolean = showUnregisteredBuilding || components.length > 0,
        extraMetadata: string[] = []
    ) => show && (
        <BuildingCustomButton
            title={title}
            metadata={[...analyzeComponents(components, building), ...extraMetadata]}
            imagePath={imagePath}
            dimmed={components.length === 0}
            to={`${base}/${path}`}
        />
    );

    return (
        <div className="bt-categories">
            <div className={`bt-category${buildingCategoryEmpty ? ' bt-category--empty' : ''}`}>
                <CategoryHeader
                    icon="/Images/UI/klimatskal.png"
                    title="Byggnad"
                    showUnregistered={showUnregisteredBuilding}
                    onToggleUnregistered={() => setShowUnregisteredBuilding(v => !v)}
                />
                <ul>
                    {/* The roof page is also the takplan (roof components on the roof drawing / satellite image), so it is offered whenever there is something to draw (D18) */}
                    {typeButton(
                        roofComponents,
                        translate(BaseType.Roof),
                        'Basetypes/roof.png',
                        'roof',
                        showUnregisteredBuilding || roofComponents.length > 0 || hasRoofPlanView(model.structure),
                        [...childCounts(roofComponents), ...(currentRoofPlanBlueprint(model.structure) ? ['Takritning'] : [])]
                    )}
                    {typeButton(roofSafetyComponents, translate(BaseType.RoofSafety), 'Basetypes/roof_safety.png', 'roof-safety')}
                    {typeButton(drainageComponents, `${translate(BaseType.RoofDrainage)} (Yttre VA)`, 'Basetypes/roof_drainage.png', 'roof_drainage')}
                    {typeButton(chimneyComponents, translate(BaseType.Chimney), 'Basetypes/chimney.png', 'chimney')}
                    {typeButton(facadeComponents, translate(BaseType.Facade), 'Basetypes/facade.png', 'facade', undefined, childCounts(facadeComponents))}
                    {typeButton(balconyComponents, translate(BaseType.Balcony), 'Basetypes/balcony.png', 'balcony')}
                    {typeButton(foundationComponents, translate(BaseType.Foundation), 'Basetypes/foundation.png', 'foundation')}
                    {typeButton(groundDrainageComponents, `${translate(BaseType.GroundDrainage)} (Yttre VA)`, 'Basetypes/ground_drainage.png', 'ground_drainage')}
                    {typeButton(riserComponents, translate(BaseType.Riser), 'Basetypes/riser.png', 'riser')}
                    {typeButton(miscellaneousComponents, translate(BaseType.Miscellaneous), 'Basetypes/miscellaneous.png', 'miscellaneous')}
                    {windowCount > 0 && (
                        <BuildingCustomButton
                            title="Fönster"
                            metadata={[`${windowCount} st`]}
                            imagePath="types/window.png"
                            to={`${base}/windows`}
                        />
                    )}
                    {doorCount > 0 && (
                        <BuildingCustomButton
                            title="Dörrar"
                            metadata={[`${doorCount} st`]}
                            imagePath="types/door.png"
                            to={`${base}/doors`}
                        />
                    )}
                    {sheetMetalCount > 0 && (
                        <BuildingCustomButton
                            title={translate(BaseType.SheetMetal)}
                            metadata={[`${sheetMetalCount} st`]}
                            imagePath="materials/sheet_metal.png"
                            to={`${base}/sheet-metal`}
                        />
                    )}
                    {buildingCategoryEmpty && !showUnregisteredBuilding && <EmptyCategoryCard text="Inga byggnadsdelar registrerade" />}
                </ul>
            </div>

            <div className={`bt-category${levelsCategoryEmpty ? ' bt-category--empty' : ''}`}>
                <CategoryHeader
                    icon="/Images/UI/vaningsplan.png"
                    title="Våningsplan"
                    showUnregistered={showUnregisteredVaningsplan}
                    onToggleUnregistered={() => setShowUnregisteredVaningsplan(v => !v)}
                />
                <ul>
                    {Object.entries(model.structure?.levels || {})
                        .sort(([, levelA], [, levelB]) => levelB.number - levelA.number)
                        .map(([key, level]) => {
                            const roomsForLevel = allRooms.filter(room => room.levelKey === level.key);
                            let imageString = 'mellanvaning.png';
                            if (level.number < 1) {
                                imageString = 'nedervaning.png';
                            } else if (level.number > 1) {
                                imageString = 'overvaning.png';
                            }
                            return (
                                <BuildingCustomButton
                                    key={key}
                                    title={new LevelImpl(level).displayName() || translate(level.type) || 'Odefinierat rum'}
                                    metadata={[`Antal rum: ${roomsForLevel.length}`]}
                                    imagePath={imageString}
                                    to={`${base}/level/${key}`}
                                />
                            );
                        })}
                    {roomsWithoutLevel.length > 0 && (
                        <BuildingCustomButton
                            title="Ingen våning"
                            metadata={[`Antal rum: ${roomsWithoutLevel.length}`]}
                            imagePath="placeholder.png"
                            to={`${base}/level/no-level`}
                        />
                    )}
                    {levelsCategoryEmpty && <EmptyCategoryCard text="Inga våningsplan registrerade" />}
                </ul>
            </div>

            <div className={`bt-category${outdoorCategoryEmpty ? ' bt-category--empty' : ''}`}>
                <CategoryHeader
                    icon="/Images/UI/utemiljo.png"
                    title="Utemiljö"
                    showUnregistered={showUnregisteredUtemiljo}
                    onToggleUnregistered={() => setShowUnregisteredUtemiljo(v => !v)}
                />
                <ul>
                    {Object.values(OutdoorCategory)
                        .sort((a, b) => translate(a).localeCompare(translate(b), 'sv'))
                        .filter(category => showUnregisteredUtemiljo || outdoorCategoryCounts[category] > 0)
                        .map(category => (
                            <BuildingCustomButton
                                key={category}
                                title={translate(category)}
                                metadata={[`${outdoorCategoryCounts[category]} st`]}
                                to={`${base}/outdoor/${category}`}
                                imagePath={`OutdoorCategories/${category}.png`}
                                dimmed={outdoorCategoryCounts[category] === 0}
                            />
                        ))}
                    {outdoorUncategorizedCount > 0 && (
                        <BuildingCustomButton
                            title="Okategoriserat"
                            metadata={[`${outdoorUncategorizedCount} st`]}
                            to={`${base}/outdoor/uncategorized`}
                            imagePath="OutdoorCategories/uncategorized.png"
                        />
                    )}
                    {outdoorCategoryEmpty && !showUnregisteredUtemiljo && <EmptyCategoryCard text="Ingen utemiljö registrerad" />}
                </ul>
            </div>

            <div className={`bt-category${utilityCategoryEmpty ? ' bt-category--empty' : ''}`}>
                <CategoryHeader
                    icon="/Images/UI/fastighetsteknik.png"
                    title="Fastighetsteknik"
                    showUnregistered={showUnregisteredFastighetsteknik}
                    onToggleUnregistered={() => setShowUnregisteredFastighetsteknik(v => !v)}
                />
                <ul>
                    {Object.values(UtilityCategory)
                        .sort((a, b) => translate(a).localeCompare(translate(b), 'sv'))
                        .filter(category => showUnregisteredFastighetsteknik || categoryCounts[category] > 0)
                        .map(category => (
                            <BuildingCustomButton
                                key={category}
                                title={translate(category)}
                                metadata={[`${categoryCounts[category]} st`]}
                                to={`${base}/utilities/${category}`}
                                imagePath={`UtilityCategories/${category}.png`}
                                dimmed={categoryCounts[category] === 0}
                            />
                        ))}
                    {uncategorizedCount > 0 && (
                        <BuildingCustomButton
                            title="Okategoriserat"
                            metadata={[`${uncategorizedCount} st`]}
                            to={`${base}/utilities/uncategorized`}
                            imagePath="UtilityCategories/uncategorized.png"
                        />
                    )}
                    {utilityCategoryEmpty && !showUnregisteredFastighetsteknik && <EmptyCategoryCard text="Ingen fastighetsteknik registrerad" />}
                </ul>
            </div>

            <div className={`bt-category${fixedFurnishingComponents.length === 0 ? ' bt-category--empty' : ''}`}>
                <CategoryHeader icon="/Images/UI/fastinredning.png" title={translate(BaseType.FixedFurnishing)} />
                <ul>
                    {fixedFurnishingComponents.length > 0 ? (
                        <BuildingCustomButton
                            title={translate(BaseType.FixedFurnishing)}
                            metadata={[
                                `${fixedFurnishingComponents.length} st`,
                                ...(unplacedFixedFurnishingCount > 0 ? [`${unplacedFixedFurnishingCount} utan rum`] : []),
                            ]}
                            imagePath="Basetypes/fixed_furnishing.png"
                            to={`${base}/fixed_furnishing`}
                        />
                    ) : (
                        <EmptyCategoryCard text="Ingen fast inredning registrerad" />
                    )}
                </ul>
            </div>

            {parkingEnabled && (
            <div className={`bt-category${parkingAreas.length === 0 ? ' bt-category--empty' : ''}`}>
                <CategoryHeader icon="/Images/UI/parkering.png" title="Parkering" />
                <ul>
                    {parkingAreas.length === 0 ? (
                        <EmptyCategoryCard
                            text="Ingen parkering registrerad"
                            addLabel="Lägg till parkering"
                            addTo={`${base}/parking`}
                        />
                    ) : parkingAreas.map(area => {
                        const spots = Object.values(area.spots || {});
                        const chargers = spots.filter(spotHasCharger).length;
                        return (
                            <BuildingCustomButton
                                key={area.key}
                                title={parkingAreaDisplayName(area)}
                                metadata={[
                                    translateParkingAreaKind(area.kind),
                                    `Antal platser: ${spots.length}`,
                                    `Lediga: ${vacantSpotCount(area)}`,
                                    ...(chargers > 0 ? [`Laddare: ${chargers}`] : []),
                                ]}
                                to={`${base}/parking/${area.key}`}
                            />
                        );
                    })}
                </ul>
            </div>

            )}

            {storageEnabled && (
            <div className={`bt-category${storageLevels.length === 0 ? ' bt-category--empty' : ''}`}>
                <CategoryHeader icon="/Images/UI/forrad.png" title="Förråd" />
                <ul>
                    {storageLevels.length === 0 ? (
                        <EmptyCategoryCard
                            text="Inga förråd registrerade"
                            addLabel="Lägg till förråd"
                            addTo={`${base}/storage-units`}
                        />
                    ) : storageLevels.map(({ level, units: onLevel }) => {
                        const area = totalStorageUnitArea(onLevel);
                        return (
                            <BuildingCustomButton
                                key={level.key}
                                title={levelDisplayName(level)}
                                metadata={[
                                    `Antal förråd: ${onLevel.length}`,
                                    `Lediga: ${vacantStorageUnitCount(onLevel)}`,
                                    ...(area > 0 ? [`Total yta: ${formatArea(area)}`] : []),
                                ]}
                                to={`${base}/storage-units?level=${level.key}`}
                            />
                        );
                    })}
                </ul>
            </div>

            )}

            {unitsEnabled && (
            <div className={`bt-category${units.length === 0 ? ' bt-category--empty' : ''}`}>
                <CategoryHeader icon="/Images/UI/forrad.png" title="Lägenheter & lokaler" />
                <ul>
                    {units.length === 0 ? (
                        <EmptyCategoryCard
                            text="Inga lägenheter eller lokaler registrerade"
                            addLabel="Lägg till lägenheter och lokaler"
                            addTo={`${base}/units`}
                        />
                    ) : [UnitKind.Apartment, UnitKind.Commercial].map(kind => {
                        const ofKind = units.filter(unit => unit.kind === kind);
                        if (ofKind.length === 0) return null;
                        const hyresratt = ofKind.filter(unit => unit.tenure === UnitTenure.Hyresratt);
                        const bostadsratt = ofKind.filter(unit => unit.tenure === UnitTenure.Bostadsratt);
                        const area = totalUnitArea(ofKind);
                        return (
                            <BuildingCustomButton
                                key={kind}
                                title={kind === UnitKind.Apartment ? 'Lägenheter' : 'Lokaler'}
                                metadata={[
                                    `Antal: ${ofKind.length}`,
                                    ...(hyresratt.length > 0 ? [`Hyresrätter: ${hyresratt.length} · Lediga: ${vacantUnitCount(hyresratt)}`] : []),
                                    ...(bostadsratt.length > 0 ? [`Bostadsrätter: ${bostadsratt.length}`] : []),
                                    ...(area > 0 ? [`Total yta: ${formatArea(area)}`] : []),
                                ]}
                                to={`${base}/units`}
                            />
                        );
                    })}
                </ul>
            </div>
            )}
        </div>
    );
};

export default ComponentCategoriesTab;
